<template>
  <article
    v-if="product"
    class="group relative rounded-2xl overflow-hidden border border-red-100 bg-gradient-to-br from-red-50 to-white shadow-card hover:shadow-lg transition-shadow cursor-pointer flex flex-col sm:flex-row"
    @click="$emit('click', product)"
  >
    <!-- Image -->
    <div class="relative bg-white overflow-hidden sm:w-1/2 md:w-2/5 aspect-square sm:aspect-auto sm:min-h-[280px]">
      <img
        v-if="firstImage"
        :src="firstImage"
        :alt="product.nom"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
        decoding="async"
      />
      <span
        v-if="reductionPct > 0"
        class="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-red-500 text-white text-sm font-bold shadow"
      >−{{ Math.round(reductionPct) }}%</span>
      <span class="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-red-600 text-xs font-semibold shadow">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
        {{ $t('deal.badge') }}
      </span>
    </div>

    <!-- Body -->
    <div class="p-5 sm:p-6 flex-1 flex flex-col">
      <h3 class="text-lg sm:text-xl font-bold text-slate-900 line-clamp-2">{{ product.nom || product.title }}</h3>
      <p v-if="product.title && product.title !== product.nom" class="mt-1 text-sm text-slate-600 line-clamp-2">{{ product.title }}</p>

      <div class="mt-4 flex items-baseline gap-3 flex-wrap">
        <span class="text-2xl sm:text-3xl font-extrabold text-red-600">{{ formatCurrency(discountedPrice) }}</span>
        <span class="text-base text-slate-400 line-through">{{ formatCurrency(product.prix) }}</span>
      </div>

      <!-- Countdown -->
      <div v-if="endsAt" class="mt-4">
        <p class="text-xs uppercase tracking-wide text-red-600 font-semibold mb-1">{{ $t('deal.endsIn') }}</p>
        <div class="flex items-center gap-2">
          <span v-for="(part, i) in countdownParts" :key="i"
                class="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold tabular-nums min-w-[3rem] text-center">
            {{ part.value }}<span class="ml-0.5 text-[10px] font-medium text-slate-300 uppercase">{{ part.unit }}</span>
          </span>
        </div>
      </div>

      <button
        type="button"
        class="mt-5 sm:mt-auto h-11 px-5 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 self-start"
        @click.stop="$emit('click', product)"
      >
        {{ $t('deal.cta') }}
      </button>
    </div>
  </article>
</template>

<script>
export default {
  name: 'DealOfTheDayCard',
  emits: ['click'],
  props: {
    product: { type: Object, default: null },
  },
  data() {
    return {
      now: Date.now(),
      tickHandle: null,
    };
  },
  computed: {
    firstImage() {
      const imgs = this.product?.images;
      if (!Array.isArray(imgs) || !imgs.length) return null;
      const first = imgs[0];
      return typeof first === 'string' ? first : (first?.image || null);
    },
    reductionPct() {
      if (this.product?.reduction_percentage != null) return Number(this.product.reduction_percentage) || 0;
      return Number(this.product?.reduction?.percentage) || 0;
    },
    discountedPrice() {
      if (this.product?.prix_with_reduction != null) return Number(this.product.prix_with_reduction) || 0;
      const prix = Number(this.product?.prix || 0);
      return prix - prix * (this.reductionPct / 100);
    },
    endsAt() {
      const raw = this.product?.reduction?.end_date || this.product?.promotion_end;
      if (!raw) return null;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : null;
    },
    countdownParts() {
      if (!this.endsAt) return [];
      const ms = Math.max(0, this.endsAt - this.now);
      const totalSec = Math.floor(ms / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;
      const pad = (n) => String(n).padStart(2, '0');
      const parts = [];
      if (days > 0) parts.push({ value: pad(days), unit: 'j' });
      parts.push({ value: pad(hours), unit: 'h' });
      parts.push({ value: pad(mins), unit: 'm' });
      parts.push({ value: pad(secs), unit: 's' });
      return parts;
    },
  },
  mounted() {
    if (this.endsAt) {
      this.tickHandle = setInterval(() => { this.now = Date.now(); }, 1000);
    }
  },
  beforeUnmount() {
    if (this.tickHandle) clearInterval(this.tickHandle);
  },
  methods: {
    formatCurrency(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n.toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
    },
  },
};
</script>
